/**
 * Eigener Titel, Beschreibung und Canonical je Seite (SEO-Prüfung 28.10.2026, ohne Gedankenstriche).
 * Setzt die Angaben direkt im <head>; deploy/prerender_heads.js liest sie aus und erzeugt daraus je Seite eine HTML-Datei,
 * damit Google schon ohne JavaScript den richtigen Titel sieht.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { imgSet } from "@/lib/img";

const HOST = "https://www.picturebox24.de";
const SITE = "Picturebox24";
const PAGES = {
  "/": ["Fotobox und Magic Mirror mieten in NRW", "Fotobox und Magic Mirror mieten für Hochzeit, Geburtstag und Firmenfeier in NRW. Sofortdruck, individuelle Layouts und persönlicher Service aus Bottrop."],
  "/privatkunden": ["Fotobox mieten für Hochzeit und Geburtstag", "Fotobox oder Magic Mirror für Ihre Hochzeit, Ihren Geburtstag oder Ihre Feier mieten: mit Sofortdruck, eigenem Layout und Lieferung in NRW."],
  "/businesskunden": ["Fotobox mieten für Firmenevents und Messen", "Fotobox und Magic Mirror für Firmenfeiern, Messen und Events: mit Ihrem Logo im Layout, Betreuung und Rechnung auf Ihr Unternehmen."],
  "/produkte": ["Unsere Fotoboxen und der Magic Mirror", "Fotobox Premium, Magic Mirror und Zusatzleistungen wie Audio-Gästebuch im Überblick: Ausstattung, Preise und Extras."],
  "/buchen": ["Fotobox online buchen", "Wunschtermin wählen, Fotobox oder Magic Mirror aussuchen und direkt online buchen. Abholung in Bottrop oder Lieferung in NRW."],
  "/ablauf": ["So funktioniert die Fotobox-Miete", "Von der Buchung über Ihr Layout bis zur Rückgabe: So läuft die Miete einer Fotobox bei Picturebox24 ab."],
  "/layouts": ["Fotobox-Layouts für jeden Anlass", "Über 180 Fotobox-Layouts für Hochzeit, Geburtstag, Firmenevent und mehr. Direkt online ansehen und selbst anpassen."],
  "/faq": ["Häufige Fragen zur Fotobox-Miete", "Antworten zu Preisen, Lieferung, Aufbau, Ausdrucken, Layouts und Bezahlung bei der Fotobox-Miete von Picturebox24."],
  "/kontakt": ["Kontakt", "So erreichen Sie Picturebox24: Telefon 02041 7355679, E-Mail info@picturebox24.de, Im Fuhlenbrock 113, 46242 Bottrop."],
  "/hilfe": ["Hilfe zur Fotobox", "Hilfe und Anleitungen rund um Ihre gemietete Fotobox von Picturebox24."],
  "/impressum": ["Impressum", "Impressum von Picturebox24, Inhaber Kevin Kreul, Im Fuhlenbrock 113, 46242 Bottrop."],
  "/datenschutz": ["Datenschutzerklärung", "Datenschutzerklärung von Picturebox24."],
  "/agb": ["Allgemeine Geschäftsbedingungen", "Allgemeine Geschäftsbedingungen für die Fotobox-Miete bei Picturebox24."],
};

// Hauptbild je Seite (LCP), wird im Seitenkopf vorgeladen; prerender_heads.js übernimmt es in die statische Seite.
// Größenangabe muss zu imgSet(...) am Bild passen, sonst lädt der Browser doppelt.
const PRELOAD = {
  "/businesskunden": ["/ext/stock/photo-1768508947486-f54df8b43869.webp", "(max-width: 768px) 100vw, 50vw"],
  "/produkte": ["/ext/ybgnwvuj_ChatGPT_Image_2._Juli_2026_17_44_17.png", "(max-width: 768px) 100vw, 50vw"],
};

function setMeta(attr, key, value) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", value);
}

export default function RouteSeo() {
  const { pathname } = useLocation();
  useEffect(() => {
    const path = pathname.replace(/\/$/, "") || "/";
    const p = PAGES[path];
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!p) { if (link) link.remove(); return; }
    const title = path === "/" ? `${SITE}: ${p[0]}` : `${p[0]} · ${SITE}`;
    document.title = title;
    setMeta("name", "description", p[1]);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", p[1]);
    setMeta("property", "og:url", HOST + (path === "/" ? "/" : path));
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", p[1]);
    if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
    link.setAttribute("href", HOST + (path === "/" ? "/" : path));
    link.setAttribute("data-rh", "true");  // für prerender_heads.js
    document.head.querySelectorAll('link[rel="preload"][data-route-preload]').forEach((l) => l.remove());
    const pre = PRELOAD[path];
    if (pre) {
      const set = imgSet(pre[0], pre[1]);
      const l = document.createElement("link");
      l.setAttribute("rel", "preload");
      l.setAttribute("as", "image");
      l.setAttribute("href", set.src);
      if (set.srcSet) { l.setAttribute("imagesrcset", set.srcSet); l.setAttribute("imagesizes", set.sizes); }
      l.setAttribute("fetchpriority", "high");
      l.setAttribute("data-route-preload", "true");
      l.setAttribute("data-rh", "true");
      document.head.appendChild(l);
    }
  }, [pathname]);
  return null;
}
